import * as THREE from 'three';
import { G, K_BEV } from '../../render/scene';
import { rr, rri } from '../../core/rng';
import { beamTex } from '../../render/ground';
import { put } from './ruin-kit';

/* ============================================================
   敘事地標 —— 「這裡發生過什麼」
   ============================================================

   使用者 2026-08-04：「造景／光影／**故事性**／材質／質感／色調都要做到
   非常不突兀又非常吸引人」，同一輪補充：「場景也不能設計過於擁擠，
   畢竟是類倖存者遊戲，空曠的場地很重要」。

   這兩句合起來就是這個檔存在的理由：**故事性不能用密度換**。
   所以這裡的東西分兩種，而且以第一種為主：

     ① **走得過去的裝飾**（不進 `obstacles`）——車隊、路障、撞擊溝、路牌。
        它們在畫面上是內容，在玩法上是空地。空曠場地的資訊量要從這裡來，
        不是從多蓋幾棟樓來。
     ② **一張圖只有一件的地標**（進 `obstacles`）——墜落的構造體、探照燈塔。
        它們的工作是「從很遠就看得到，而且看得出方向」，讓 480 m 見方的
        空地有座標感 —— 沒有地標的開放場地，走十秒跟走一分鐘長得一樣。

   ## 為什麼車隊不做成掩體

   `field.test.ts` 守著「場上每一個構件都高於 `WALL_MIN_H`」——矮掩體會讓
   「走不過也飛不上去」那條規格靜靜消失。車隊做成掩體就得破那條規格，
   而它換來的玩法（躲在車後面）跟現有的量體重複。**所以車隊純粹是畫面。**
   ============================================================ */

/** 火要燒在有東西可燒的地方。這裡登記「燒毀的車輛」等火點，
    `field.ts` 的 `buildFires` 會優先用它們 —— 憑空燒起來的一團橘光讀成
    特效，燒在一台翻覆的軍卡上才讀成撤離失敗。 */
var _pins: any[] = [];
export function resetPins() { _pins.length = 0; }
export function firePins() { return _pins; }

/* ---------- ① 走得過去的裝飾 ---------- */

/** 一台車（廢棄／燒毀／翻覆）。長 5–7 m，比機體矮一半以上 ——
    尺度參照物：玩家機體 4.4 m，一台車看起來要「踩得過去」。 */
function car(x, z, ry, kind) {
  var L = rr(5.2, 7.0), W = rr(2.2, 2.7), H = rr(1.6, 2.1);
  var body = kind === 'burnt' ? 'conD' : (rr(0, 1) < .5 ? 'rust' : 'steel');
  var roll = kind === 'flip' ? rr(2.6, 3.5) : rr(-.05, .05);
  var yy = kind === 'flip' ? H * .62 : H * .5;
  put(body, G.box, 'box', L, H, W, x, yy, z, 0, ry, roll, 2.4);
  /* 車廂／駕駛室：**兩節而不是一塊**，那是「車」與「箱子」的差別 */
  put(body, G.box, 'box', L * .42, H * .62, W * .92,
    x + Math.cos(ry) * L * .26, yy + H * .58, z - Math.sin(ry) * L * .26, 0, ry, roll, 2.2);
  /* 窗：深色一條。燒毀的車沒有窗（都爆了） */
  if (kind !== 'burnt')
    put('trim', G.box, 'box', L * .40, H * .30, W * .96,
      x + Math.cos(ry) * L * .26, yy + H * .72, z - Math.sin(ry) * L * .26, 0, ry, roll);
  /* 輪：翻覆的車輪朝天，那是一眼讀得出「翻了」的線索 */
  for (var i = 0; i < 4; i++) {
    var ox = (i < 2 ? 1 : -1) * L * .32, oz = (i % 2 ? 1 : -1) * W * .52;
    put('trim', G.cyl, 'cyl', 1.05, .5, 1.05,
      x + ox * Math.cos(ry) + oz * Math.sin(ry), kind === 'flip' ? H * 1.15 : .52,
      z - ox * Math.sin(ry) + oz * Math.cos(ry), 0, ry, Math.PI / 2);
  }
  if (kind === 'burnt') _pins.push({ x: x, z: z });
}

/** 混凝土隔離墩：一排斜著的、有幾個被撞歪。 */
function barrier(x, z, ry, n) {
  for (var i = 0; i < n; i++) {
    var t = (i - (n - 1) / 2) * 3.1 + rr(-.3, .3);
    var bx = x + Math.cos(ry) * t, bz = z - Math.sin(ry) * t;
    var knock = rr(0, 1) < .22;
    put('con', G.bev, 'cyl', 1.5 * K_BEV, 1.5, 3.0 * K_BEV, bx, knock ? .6 : .75, bz,
      knock ? rr(-1.6, 1.6) : 0, ry + rr(-.12, .12), knock ? rr(-1.5, 1.5) : 0, 3);
    /* 頂緣的警示帶：比本體窄一圈，露出來的只有薄薄一條（不然整片反白） */
    if (!knock) put('haz', G.box, 'box', 1.32, .34, 2.7, bx, 1.42, bz, 0, ry, 0, 1.4);
  }
}

/**
 * 撤離車隊：**這一關的故事**。停在半路上的車隊、被推開的路障、
 * 燒毀的頭車 —— 一眼讀得出「有人試著從這裡離開，沒走成」。
 * 全部走得過去（見檔頭），所以它加的是資訊量不是擁擠感。
 */
export function convoy(x, z, ry) {
  var n = rri(3, 5);
  for (var i = 0; i < n; i++) {
    var t = (i - (n - 1) / 2) * rr(9, 13);
    var jitter = rr(-2.6, 2.6);
    var kind = i === 0 ? 'burnt' : (i === n - 1 && rr(0, 1) < .5 ? 'flip' : 'idle');
    car(x + Math.cos(ry) * t + Math.sin(ry) * jitter,
      z - Math.sin(ry) * t + Math.cos(ry) * jitter,
      ry + rr(-.42, .42), kind);
  }
  /* 車隊前面那道沒推開的路障 —— 因果關係要讀得出來：
     **路障擋在前方，所以車隊停在這裡**。放在後面就只是散落的雜物。 */
  var hx = x + Math.cos(ry) * (n * 6 + 8), hz = z - Math.sin(ry) * (n * 6 + 8);
  barrier(hx, hz, ry + Math.PI / 2, rri(5, 8));
  /* 散落的行李與貨箱：走得過去、但腳邊有東西 */
  for (var k = 0; k < 14; k++) {
    var a = rr(0, 6.283), r2 = rr(4, n * 7);
    put(rr(0, 1) < .5 ? 'rust' : 'conD', G.box, 'box', rr(.8, 2.2), rr(.6, 1.4), rr(.8, 1.8),
      x + Math.cos(a) * r2, rr(.3, .7), z + Math.sin(a) * r2, rr(-.2, .2), rr(0, 6.283), rr(-.2, .2), 2);
  }
}

/** 倒下的高架路牌：撤離指示。牌面朝著鏡頭那一側還留著一條沒壞的燈條。 */
export function signboard(x, z, ry) {
  var lean = rr(.55, .95);
  for (var k = 0; k < 2; k++) {
    var ox = (k ? 1 : -1) * 4.2;
    put('steel', G.box, 'box', .6, 9, .6,
      x + ox * Math.cos(ry) - Math.sin(ry) * 2.4, 4.0, z - ox * Math.sin(ry) - Math.cos(ry) * 2.4,
      lean * Math.sin(ry), ry, lean * Math.cos(ry), 3);
  }
  var px = x + Math.sin(ry) * 5.4, pz = z + Math.cos(ry) * 5.4;
  put('con', G.box, 'box', 11, 3.4, .4, px, 6.4, pz, lean * .82, ry, 0, 4);
  put('lit', G.box, 'box', 10.2, .26, .2, px, 5.0, pz, lean * .82, ry, 0);
}

/* ---------- ② 一張圖只有一件的地標 ---------- */

/**
 * 墜落的構造體：一節插進地面的艙段。**第一關的地標** ——
 * 它有方向（尾端翹起來、機首埋在自己犁出來的溝裡），所以玩家在
 * 480 m 見方的空地上永遠知道自己在哪一邊。
 *
 * 佔地很寬（~44 m），所以跟倒塌大樓一樣走「特殊件」的投放路徑：
 * **自己做間距檢查、自己登記位置**（它比呼叫端更清楚自己多大）。
 */
export function wreck(x, z, ry, gapOK, place, solid, gap) {
  if (!gapOK(x, z, 26, 26, gap)) return false;
  /* ⚠️ 長度與粗細有上限，理由跟倒塌大樓同一條：三節連在一起是一塊
     **連續的實心量體**，60 m 的取樣窗一次就吃掉一大半。實測 26–34 m
     長 × 半徑 6.2 讓熔煉層的視窗覆蓋峰值破到 23.2%（規格 ≤ 22%）。 */
  var L = rr(22, 27), R0 = rr(3.8, 4.8), pitch = rr(.28, .42);
  var c = Math.cos(ry), s = Math.sin(ry);
  /* 三節艙身，越往尾端越細；整根往上翹 —— 一根等粗的圓柱讀成水管 */
  for (var i = 0; i < 3; i++) {
    var t = (i - 1) * L / 3, rad = R0 * (1 - i * 0.14);
    var yy = 1.2 + t * Math.sin(pitch) + L * 0.16;
    /* ⚠️ 外皮不可以用 `facA/B/C`：那三個掛著窗戶的 emissiveMap，
       貼在艙身上會變成「一艘有住戶的太空船」。 */
    put(i === 1 ? 'steel' : 'con', G.bev, 'cyl', rad * 2 * K_BEV, L / 3 * 1.04, rad * 2 * K_BEV,
      x + t * c * Math.cos(pitch), yy, z - t * s * Math.cos(pitch), 0, ry, Math.PI / 2 - pitch, 6);
    /* 環肋：一節一條，讀得出是「構造體」不是石頭 */
    put('rust', G.bev, 'cyl', rad * 2.16 * K_BEV, 1.0, rad * 2.16 * K_BEV,
      x + t * c * Math.cos(pitch), yy, z - t * s * Math.cos(pitch), 0, ry, Math.PI / 2 - pitch, 2);
    /* `stand` **一定要顯式傳 false**：省略的話 `addObstacle` 會依高度
       預設成 `true`，於是「飛上去站不住」那條規格靜靜消失
       （`field.test.ts` 有一條在守它，但別的建造器沒有這個保護）。 */
    solid(x + t * c * Math.cos(pitch), z - t * s * Math.cos(pitch), ry, L / 3, rad * 2, Math.max(11, yy + rad), false);
  }
  /* 撕開的斷面：外露的骨架 */
  for (var u = 0; u < 9; u++) {
    var tt = -L / 2 - rr(0, 3);
    put('rust', G.cyl, 'cyl', .22, rr(2.4, 6.0), .22,
      x + tt * c + rr(-3, 3), 2.0 + rr(0, 3), z - tt * s + rr(-3, 3),
      rr(-.7, .7), rr(0, 6.283), rr(-.7, .7), 2);
  }
  /* 犁出來的溝：地上一條淺色的擾動帶（純裝飾，走得過去）——
     沒有這條溝，艙段讀成「有人把它放在這裡」而不是「它掉下來」。 */
  for (var d = 0; d < 16; d++) {
    var td = L / 2 + d * rr(3.2, 4.6);
    put('conD', G.box, 'box', rr(5, 9), .34, rr(7, 13),
      x + td * c + rr(-3, 3), .18, z - td * s + rr(-3, 3), 0, ry + rr(-.2, .2), 0, 6);
  }
  _pins.push({ x: x - L * 0.5 * c, z: z + L * 0.5 * s });
  place({ x: x, z: z, hw: 26, hd: 26 });
  return true;
}

/* ---------- 探照燈（動態光影 × 地標）---------- */

/** 幾道光束。**效能旋鈕**：光束是半透明加算的大面積幾何，
    在填充率上不便宜；兩道足夠讓天空有東西在動。 */
export var BEAM_N = 2;
var beams: any[] = [];
var beamGroup: THREE.Group | null = null;

export function resetBeams() { beams.length = 0; beamGroup = null; }

/**
 * 探照燈塔：16–22 m 的四腳塔 ＋ 一道緩慢掃過的光束。
 * 三件事一次做完 —— 空曠場地缺的「有東西在動」、暗畫面缺的亮部錨點、
 * 以及故事（**還有人在找東西**）。
 */
export function searchlight(group: THREE.Group, x, z, solid) {
  var h = rr(16, 22);
  for (var k = 0; k < 4; k++) {
    var ox = (k & 1 ? 1 : -1) * 1.9, oz = (k & 2 ? 1 : -1) * 1.9;
    put('steel', G.box, 'box', .55, h, .55, x + ox, h / 2, z + oz, 0, 0, 0, 4);
  }
  for (var y = 4; y < h - 1; y += 5) put('steel', G.box, 'box', 4.6, .28, 4.6, x, y, z, 0, 0, 0, 4);
  put('conD', G.box, 'box', 5.4, .5, 5.4, x, h, z, 0, 0, 0, 4);
  put('lit', G.box, 'box', 1.8, 1.1, 1.8, x, h + .8, z);
  solid(x, z, 0, 5.4, 5.4, h, false);

  var tx = beamTex(); if (!tx) return;
  if (!beamGroup) { beamGroup = new THREE.Group(); group.add(beamGroup); }
  var pivot = new THREE.Object3D(); pivot.position.set(x, h + .8, z);
  var tilt = new THREE.Object3D();
  /* 76° ＝ 幾乎水平、往下 14°。垂直打向天空的話光束跟地面沒有交點，
     俯視鏡頭下只看得到一根柱子；壓到接近水平才會在地上掃出一塊亮斑。 */
  tilt.rotation.x = 82 * Math.PI / 180;
  var LEN = 62;
  /* 錐體要**寬而淡**。窄而濃的話讀成一片白色三角形貼紙（第一版 6.4 半徑
     × 0.32 不透明度就是這樣）—— 光束在空氣裡是靠散射看到的，
     邊緣一定是糊的，而糊的來源在這裡只能靠「大面積 × 低不透明度」。 */
  var cone = new THREE.Mesh(
    new THREE.ConeGeometry(6.8, LEN, 14, 1, true),
    new THREE.MeshBasicMaterial({
      /* 探照燈是**冷白**（2026-08-04 從暖黃 `0xffc890` 換）：這是機器打的光，
         暖黃讀成鎢絲燈／舊路燈，跟機甲的冷調對不上。真正的暖色留給火與窗燈。 */
      map: tx as any, color: 0xbfd8ff, transparent: true, opacity: .055,
      blending: THREE.AdditiveBlending, depthWrite: false,
      side: THREE.DoubleSide, fog: true,
    }));
  cone.position.y = -LEN / 2;      // 圓錐頂點落在燈箱上
  cone.frustumCulled = false;
  tilt.add(cone); pivot.add(tilt); beamGroup.add(pivot);
  beams.push({ pivot: pivot, mat: cone.material, t: rr(0, 6.283), spd: rr(.11, .17) * (rr(0, 1) < .5 ? -1 : 1) });
}

/** 每幀：光束緩慢掃 ＋ 輕微的亮度呼吸。**跑在畫面幀上**（純視覺）。 */
export function animBeams(dt: number) {
  for (var i = 0; i < beams.length; i++) {
    var b = beams[i];
    b.t += dt;
    /* 等速旋轉讀成機械掃描；用 sin 讓它在兩端慢下來，讀成「有人在操作」 */
    b.pivot.rotation.y = Math.sin(b.t * b.spd) * 2.2 + i * 1.7;
    b.mat.opacity = 0.045 + 0.018 * Math.sin(b.t * 2.3 + i);
  }
}
